import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from '@/lib/gsap';
import { prefersReducedMotion } from '@/lib/lenis';
import { Logo } from './Logo';

interface PageTransitionProps {
  children: ReactNode;
}

/**
 * PageTransition — magenta curtain page swap (PRD §8.5).
 *
 * On route change:
 *   1. Curtain slides up from the bottom (y: 100% → 0%, 400ms)
 *   2. Brief hold (~150ms) so the brand mark reads
 *   3. Curtain slides off the top (y: 0% → -100%, 400ms)
 *   4. New content fades in via a CSS keyframe (keyed by pathname).
 *
 * NOTE (React 19 + GSAP compat): we deliberately do NOT wrap `children` in
 * Framer Motion's `<AnimatePresence>`. AnimatePresence's exit phase fights
 * GSAP ScrollTrigger's pin-spacer DOM reparenting, producing
 * `NotFoundError: insertBefore/removeChild` crashes under React 19's stricter
 * scheduler. The curtain alone provides the visible page-swap polish — it's
 * a fixed overlay outside the route subtree, so it never touches React's
 * reconciler.
 */
export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const curtainRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isFirstRenderRef = useRef(true);

  // Curtain timeline on every pathname change (except first mount).
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (prefersReducedMotion()) return;

    const el = curtainRef.current;
    if (!el) return;

    gsap.set(el, { yPercent: 100, display: 'flex' });

    const tl = gsap.timeline({
      onComplete: () => {
        if (el) gsap.set(el, { display: 'none' });
      },
    });

    tl.to(el, { yPercent: 0, duration: 0.4, ease: 'power3.inOut' })
      .to(
        el.querySelector('[data-curtain-mark]'),
        { opacity: 1, scale: 1, duration: 0.25, ease: 'power2.out' },
        '<0.05'
      )
      .addLabel('hold', '+=0.1')
      .to(el, { yPercent: -100, duration: 0.4, ease: 'power3.inOut' }, 'hold')
      .to(
        el.querySelector('[data-curtain-mark]'),
        { opacity: 0, duration: 0.2 },
        'hold+=0.05'
      );

    return () => {
      tl.kill();
    };
  }, [location.pathname]);

  // Re-trigger the content fade-in CSS animation on each pathname change by
  // bumping a data attribute. No React unmount/remount — DOM stays stable.
  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    node.classList.remove('qxk-page-enter');
    // Force reflow so the animation restarts.
    void node.offsetWidth;
    node.classList.add('qxk-page-enter');
  }, [location.pathname]);

  return (
    <>
      <div ref={contentRef} className="qxk-page-enter">
        {children}
      </div>

      {/* Curtain — fixed, full-bleed, above content but below cursor. */}
      <div
        ref={curtainRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[1500] flex items-center justify-center"
        style={{
          background:
            'linear-gradient(180deg, var(--color-brand-magenta) 0%, var(--color-brand-navy) 100%)',
          display: 'none',
          transform: 'translateY(100%)',
        }}
      >
        <div
          data-curtain-mark
          style={{
            opacity: 0,
            transform: 'scale(0.9)',
          }}
        >
          <Logo variant="compact" />
        </div>
      </div>
    </>
  );
}
